import { Button, Popover } from 'antd';
import { useRef, useState } from 'react';
import { useUnit } from 'effector-react';
import { SendOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ChatIcon from './ChatIcon.jsx';
import style from './ChatModal.module.css';
import {
  $activeCity,
  $chatMessages,
  $chatOpen,
  $isChatFilters,
  $language,
  applyChatHexagonsEv,
  revertChatFiltersEv,
  saveChatHexagonsEv,
  setChatOpenEv,
  setMessagesEv,
} from '../../models/logicModel/index.js';
import ai_json from '../../dictionaries/AI.json';

function ChatModal() {
  const open = useUnit($chatOpen);
  const setOpen = useUnit(setChatOpenEv);
  const msgContainer = useRef();
  const [input, setInput] = useState('');
  const messages = useUnit($chatMessages);
  const isChatFilters = useUnit($isChatFilters);
  const revertChatFilters = useUnit(revertChatFiltersEv);
  const applyChatHexagons = useUnit(applyChatHexagonsEv);
  const [isTyping, setIsTyping] = useState(false);
  const setMessages = useUnit(setMessagesEv);
  const [threadId, setThreadId] = useState(null);
  const language = useUnit($language);
  const saveChatHexagons = useUnit(saveChatHexagonsEv);
  const activeCity = useUnit($activeCity);
  const { t } = useTranslation();

  function processText(input) {
    // Разбиваем текст на строки
    const lines = input.split(/(?<=\\.)\\d+\\.\\s/); // Разделяем по номеру с точкой и пробелом
    const result = [];

    lines.forEach((line) => {
      // Убираем `**` и заменяем содержимое внутри `【】` на пустую строку
      const cleanedLine = line
        .replace(/\\*\\*/g, '')
        .replace(/【[^】]*】/g, '')
        .replace(/\*\*(.*?)\*\*/g, '<b>$1</b>')
        .replace(/(\s\d+\.)/g, '<br/>$1')
        .replace(/(\s-\s)/g, '<br/>$1');

      if (cleanedLine.trim()) {
        result.push(cleanedLine.trim());
      }
    });

    // Возвращаем текст с новой строки для каждого пункта
    return result.join('\\n');
  }

  const animateIncomingMessage = (data, newMessages) => {
    let index = 0;
    if (data.zooms_list) {
      saveChatHexagons({
        message_index: newMessages.filter((item) => item.zooms_list).length + 1,
        map_data: data.zooms_list,
      });
    }
    if (data.text.length > 0) {
      const intervalId = setInterval(() => {
        setMessages([
          ...newMessages,
          {
            text: processText(data.text.slice(0, index + 1)),
            fromUser: false,
            map_data: data.zooms_list,
            // FIXME allows to recognize which map data to display
            message_index:
              newMessages.filter((item) => item.map_data).length + 1,
          },
        ]);
        index++;
        if (msgContainer.current) {
          msgContainer.current.scrollTop = msgContainer.current.scrollHeight;
        }
        if (index === data.text.length) {
          clearInterval(intervalId);
        }
      }, 5); // Скорость печати в миллисекундах
    } else {
      setMessages([
        ...newMessages,
        {
          text: data.text,
          fromUser: false,
          map_data: data.zooms_list,
          // FIXME allows to recognize which map data to display
          message_index: newMessages.filter((item) => item.map_data).length + 1,
        },
      ]);
      if (msgContainer.current) {
        msgContainer.current.scrollTop = msgContainer.current.scrollHeight;
      }
    }
  };

  const sendMessage = async () => {
    if (!input.trim()) return;

    // Отображаем исходящее сообщение
    const newMessages = [...messages, { text: input, fromUser: true }];
    setMessages(newMessages);
    setInput('');
    setIsTyping(true);

    if (msgContainer.current) {
      setTimeout(
        () =>
          (msgContainer.current.scrollTop = msgContainer.current.scrollHeight)
      );
    }

    // Отправляем сообщение в API
    try {
      const response = await new Promise((resolve) =>
        setTimeout(() => {
          const message = ai_json.find((item) =>
            input.toLowerCase().includes(item.key)
          );
          return resolve({
            ...message,
            text: message[`text_${language}`],
          });
        }, 1000)
      );

      // Сохраняем thread_id при первом ответе
      if (!threadId) {
        setThreadId(1);
      }

      // Анимация печати входящего сообщения
      animateIncomingMessage(response, newMessages);
    } catch (error) {
      console.error('Ошибка при запросе API:', error);
    } finally {
      setIsTyping(false);
    }
  };

  const popoverContent = (
    <div className={style.chat_container} ref={msgContainer}>
      <div className={style.messages_container}>
        {messages.map((msg, index) => (
          <div
            key={index}
            className={[
              style.message,
              msg.fromUser ? style.user_message : style.api_message,
            ].join(' ')}
          >
            <p
              className={style.messageText}
              dangerouslySetInnerHTML={{ __html: msg.text }} // Используем dangerouslySetInnerHTML для отображения HTML
            />
            {msg.map_data && Object.keys(msg.map_data).length > 0 && (
              <Button
                style={{
                  margin: '10px auto 0 auto',
                  display: 'block',
                }}
                onClick={
                  isChatFilters
                    ? () => revertChatFilters()
                    : () => applyChatHexagons(msg.message_index)
                }
              >
                {isChatFilters ? t('show_on_map') : t('back_to_city')}
              </Button>
            )}
          </div>
        ))}
        {isTyping && (
          <div className={style.typing_indicator}>
            <p>Typing...</p>
          </div>
        )}
      </div>
      <div className={style.input_container}>
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t('ask_assistant')}
          className={style.input}
          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
        />
        <Button
          className={style.send_button}
          onClick={sendMessage}
          icon={<SendOutlined />}
        />
      </div>
    </div>
  );

  return (
    <Popover content={popoverContent} open={open} placement="topRight">
      <Button
        icon={<ChatIcon />}
        onClick={() => setOpen(!open)}
        shape="circle"
        className={style.chat_button}
      />
    </Popover>
  );
}

export default ChatModal;
